import {
  Price_CNTR,
  Price_Normal,
  Price_Range,
  Price_Service,
} from "@pages/offerCreate/types";

export type CustomsOffice = {
  id: number;
  code: string;
  note: string | null;
  title: string;
  city: {
    id: number;
    name: string;
    province: string;
    country: string;
    post_code: string;
    title: string;
  };
  deleted_at: string | null;
};

export enum CustomsOfficeColumn {
  ID = "id",
  CODE = "code",
  NOTE = "note",
  TITLE = "title",
  CITY = "city",
  DELETED = "deleted",
}

export type OfficeGuarantee = {
  id: number;
  office_id: number;
  fiscal_warehouse_a11_ter?: string | null;
  a11_ter_value_euro?: string | null;
  a11_ter_expire_date?: string | null;
  customs_office?: CustomsOffice | null;
  deleted_at?: string | null;
};

export type AssignedOffice = {
  id: number;
  name: string;
  country: string;
  delivery_email: string;
  abbreviation: string;
  deferred_account_dpo_number?: string | null;
  dpo_vat_value_euro?: string | null;
  dpo_duty_value_euro?: string | null;
  dpo_expire_date?: string | null;
  email?: string | null;
  guarantees?: OfficeGuarantee[];
  deleted_at: string;
};

export enum AssignedOfficeColumn {
  ID = "id",
  NAME = "name",
  COUNTRY = "country",
  DELIVERY_EMAIL = "delivery_email",
  ABBREVIATION = "abbreviation",
  DEFERRED_ACCOUNT_DPO_NUMBER = "deferred_account_dpo_number",
  DPO_VAT_VALUE_EURO = "dpo_vat_value_euro",
  DPO_DUTY_VALUE_EURO = "dpo_duty_value_euro",
  DPO_EXPIRE_DATE = "dpo_expire_date",
  EMAIL = "email",
  DELETED = "deleted",
}

export type Warehouses = {
  id: number;
  name: string;
  address: string;
  fiscal_warehouse_code: string;
  bonded_warehouse_code: string;
  authorization_number_h2: string | null;
  customs_decision_h2: string | null;
  guarantee_a11_ter_h2: string | null;
  city: {
    id: number;
    name: string;
    province: string;
    country: string;
    post_code: string;
    title: string;
  };
  port: {
    id: number;
    name: string;
    country: string;
    title: string;
  };
  supplier: {
    id: number;
    name: string;
  };
  deleted_at: string | null;
  opening_hours: string;
};

export enum WarehousesColumn {
  ID = "id",
  PORT = "port",
  SUPPLIER = "supplier",
  NAME = "name",
  ADDRESS = "address",
  POST_CODE = "loading_address_post_code",
  CITY = "loading_address_city",
  PROVINCE = "loading_address_province",
  COUNTRY = "loading_address_country",
  HOURS = "opening_hours",
  FISCAL_WAREHOUSE = "fiscal_warehouse",
  FISCAL_WAREHOUSE_CODE = "fiscal_warehouse_code",
  BONDED_WAREHOUSE = "bonded_warehouse",
  BONDED_WAREHOUSE_CODE = "bonded_warehouse_code",
  AUTHORIZATION_NUMBER_H2 = "authorization_number_h2",
  CUSTOMS_DECISION_H2 = "customs_decision_h2",
  GUARANTEE_A11_TER_H2 = "guarantee_a11_ter_h2",
  DELETED = "deleted",
}

export type Vessel = {
  id: number;
  name: string;
  type: string;
  imo: string;
  deleted_at: string | null;
};

export enum VesselColumn {
  ID = "id",
  NAME = "name",
  TYPE = "type",
  IMO = "imo",
  DELETED = "deleted",
}

export type Banks = {
  id: number;
  name: string;
  address: string;
  city: {
    id: number;
    name: string;
    province: string;
    country: string;
    post_code: string;
    title: string;
  };
  deleted_at: string | null;
};

export enum BanksColumn {
  ID = "id",
  NAME = "name",
  ADDRESS = "address",
  COUNTRY = "country",
  CITY = "city",
  POST_CODE = "post_code",
  DELETED = "deleted",
}

export enum TruckColumn {
  ID = "id",
  NAME = "name",
  NAME_IT = "name_it",
  SERVICE = "service",
  MACRO_CATEGORY = "macro_category",
  DELETED = "deleted",
}

export type Truck = {
  id: number;
  name: string;
  service: {
    id: 0;
    type: string;
    route: string;
    flow_of_goods: string;
    description: string;
    description_it: string;
    comment: string;
    price_access_control: string[];
  };
  deleted_at: string | null;
};

export type Commodities = {
  id: number;
  name: string;
  name_it: string;
  parent: {
    id: number;
    name: string;
    name_it: string;
  };
  deleted_at: string | null;
};

export enum CommoditiesColumn {
  ID = "id",
  NAME = "name",
  NAME_IT = "name_it",
  IS_GROUP = "parent",
  DELETED = "deleted",
}

export type Suppliers = {
  id: number;
  city_id: number;
  name: string;
  company_type: string[];
  address: string;
  transporter_registry_no: string;
  note: string;
  city: {
    id: number;
    name: string;
    province: string;
    country: string;
    post_code: string;
    title: string;
  };
  vat_number: string;
  email: string;
  terms_of_payment: {
    id: number;
    name: string;
  };
  deleted_at: string | null;
};

export enum SuppliersColumn {
  ID = "id",
  NAME = "name",
  COMPANY_TYPE_OVERLAP = "company_type",
  ADDRESS = "address",
  CITY = "city",
  LEGAL_COUNTRY = "legal_country",
  EMAIL = "email",
  NOTE = "note",
  PROVINCE = "province",
  POST_CODE = "post_code",
  VAT_NUMBER = "vat_number",
  TERMS = "terms_of_payment",
  TRANSPORTER_REGISTRY_NO = "transporter_registry_no",
  DELETED = "deleted",
}

export type ServiceMaster = {
  id: number;
  type: string;
  route: string;
  flow_of_goods: string;
  service_code: string;
  description: string;
  description_it: string;
  comment: string;
  price_access_control: string[];
  deleted_at: string | null;
};

export enum ServiceMasterColumn {
  ID = "id",
  TYPE = "type",
  ROUTE = "route",
  FLOW_OF_GOODS = "flow_of_goods",
  SERVICE_CODE = "service_code",
  DESCRIPTION = "description",
  DESCRIPTION_IT = "description_it",
  COMMENT = "comment",
  PRICE_ACCESS_CONTROL = "price_access_control",
  DELETED = "deleted",
}

export type Cities = {
  id: number;
  name: string;
  province: string;
  country: string;
  post_code: string;
  deleted_at: string | null;
};

export type Terminals = {
  id: number;
  name: string;
  port_id: number;
  fiscal_warehouse_code: string;
  bonded_warehouse_code: string;
  port: {
    id: number;
    name: string;
    country: string;
    title: string;
  };
  supplier: {
    id: number;
    name: string;
  };
  deleted_at: string | null;
};

export enum TerminalsColumn {
  ID = "id",
  NAME = "name",
  PORT_ID = "port_id",
  PORT_NAME = "port",
  SUPPLIER = "supplier",
  FISCAL_WAREHOUSE = "fiscal_warehouse",
  FISCAL_WAREHOUSE_CODE = "fiscal_warehouse_code",
  BONDED_WAREHOUSE = "bonded_warehouse",
  BONDED_WAREHOUSE_CODE = "bonded_warehouse_code",
  DELETED = "deleted",
}

export enum CitiesColumn {
  ID = "id",
  NAME = "name",
  PROVINCE = "province",
  COUNTRY = "country",
  POST_CODE = "post_code",
  DELETED = "deleted",
}

export enum PortsColumn {
  ID = "id",
  NAME = "name",
  COUNTRY = "country",
  DELETED = "deleted",
}

export type Ports = {
  id: number;
  name: string;
  country: string;
  deleted_at: string;
};

export enum PaymentTermsColumn {
  ID = "id",
  NAME = "name",
  NAME_IT = "name_it",
  DELETED = "deleted",
}

export type PaymentTerms = {
  id: number;
  name: string;
  name_it: string;
  deleted_at: string;
};

export enum PackageColumn {
  ID = "id",
  NAME = "name",
  DELETED = "deleted",
}

export type Package = {
  id: number;
  name: string;
  deleted_at: string | null;
};

export type Services = {
  id: number;
  description: string;
  route: string;
  flow_of_goods: string;
  deleted_at: string | null;
};

export enum ServicesColumns {
  ID = "id",
  DESCRIPTION = "description",
  ROUTE = "route",
  FLOW_OF_GOODS = "flow_of_goods",
  DELETED = "deleted",
}

export enum MACRO_CATEGORY {
  NORMAL = "Camion normale",
  CONTAINER = "Camion contenitore",
  TRAIN = "Treno",
  EXCEPTIONAL = "Camion eccezionale",
}

export type FieldControl = "textfield" | "autocomplete" | "checkbox";

export type FieldConfig = {
  field: string;
  label: string;
  control: FieldControl;
  options?: { label: string; value: string }[] | string[];
};

export type CrudEndpoints = {
  list: string;
  getOne: (id: string | number) => string;
  create: string;
  update: (id: string | number) => string;
  delete: (id: string | number) => string;
  restore: (id: string | number) => string;
};

export type SettingsEntityConfig = {
  key: string;
  label: string;
  path: string;
  endpoints: CrudEndpoints;
  columns: string[];
  filters: string[];
  fields: FieldConfig[];
};

export type PaginationModel = {
  page: number;
  pageSize: number;
};

export type FiltersType = Record<
  string,
  | string
  | string[]
  | { id: number | string | boolean; label: number | string | boolean }
  | null
>;

export type OptionType = {
  label: string;
  id: number | string;
};

export type CustomerEmail = {
  id: number;
  email: string;
  type: string;
  customer_id: number;
  deleted_at?: string | null;
};

export type CustomerAddress = {
  id: number;
  customer: {
    id: number;
    name: string;
  };
  delivery_address: string;
  delivery_city: {
    id: number;
    name: string;
    province: string;
    country: string;
    post_code: string;
    title: string;
  } | null;
  discharging_time: string | null;
  special_instruction: string | null;
  tms_special_instruction: string | null;
  destination: string;
  deleted_at?: string | null;
};

export type Customer = {
  id: number;
  name: string;
  seaway_reference?: string;
  terms_of_payment: {
    id: number;
    name: string;
  } | null;
  legal_address: string;
  legal_city: {
    id: number;
    name: string;
    province: string;
    country: string;
    post_code: string;
    title: string;
  };
  customs_representative_mandate: boolean | null;
  validity_date: string | null;
  vat_number?: string | null;
  cbam_authorization_number?: string | null;
  destination_customs_office?: string | null;
  customs_decision_h4?: string | null;
  guarantee_a11_ter_h4?: string | null;
  emails?: CustomerEmail[];
  deleted_at: string | null;
};

export enum CustomerColumn {
  ID = "id",
  NAME = "name",
  SEAWAY_REFERENCE = "seaway_reference",
  TERMS_OF_PAYMENT = "terms_of_payment",
  COMMERCIAL_EMAIL = "commercial_email",
  OPERATIVE_EMAIL = "operative_email",
  ACCOUNTING_EMAIL = "accounting_email",
  LEGAL_ADDRESS = "legal_address",
  LEGAL_ADDRESS_CITY = "legal_address_city",
  LEGAL_ADDRESS_PROVINCE = "legal_address_province",
  LEGAL_ADDRESS_COUNTRY = "legal_address_country",
  LEGAL_ADDRESS_POST_CODE = "legal_address_post_code",
  CUSTOMS_REPRESENTATIVE_MANDATE = "customs_representative_mandate",
  VALIDITY_DATE = "validity_date",
  VAT_NUMBER = "vat_number",
  CBAM_AUTHORIZATION_NUMBER = "cbam_authorization_number",
  DESTINATION_CUSTOMS_OFFICE = "destination_customs_office",
  CUSTOMS_DECISION_H4 = "customs_decision_h4",
  GUARANTEE_A11_TER_H4 = "guarantee_a11_ter_h4",
  DELIVERY_ADDRESSES = "delivery_addresses",
  DELETED = "deleted",
}

export type ProductionMethod = {
  id: number;
  name: string;
  deleted_at: string | null;
};

export enum ProductionMethodColumn {
  ID = "id",
  NAME = "name",
  DELETED = "deleted",
}

export enum APLColumn {
  description = "Description",
  ports = "Port",
  carrier = "Carrier",
  commodities = "Commodities",
  // terminal = "Terminal",
  from = "From",
  to = "To",
  comment = "Note",
  special_agreement = "Special agreement",
  expiration_date = "Expiry validity",
}

export enum PPLColumn {
  description = "Description",
  ports = "Port",
  supplier = "Supplier",
  commodities = "Commodities",
  // terminal = "Terminal",
  from = "From",
  to = "To",
  comment = "Note",
  special_agreement = "Special agreement",
  expiration_date = "Expiry validity",
}

export type PriceServiceType = {
  service_id: number;
  price_data: Price_Normal | Price_Service | Price_CNTR | Price_Range;
  price_currency: string;
  comment: string;
  expiration_date: string;

  id: number;
  description: string;
  description_it: string;
  route: string;

  from: [
    {
      id: number;
      type: string;
      name: string;
    },
  ];
  to: [
    {
      id: number;
      type: string;
      name: string;
    },
  ];
  prices_hidden: boolean;
  commodities: [
    {
      id: number;
      name: string;
      name_it: string;
    },
  ];
  ports: [
    {
      id: number;
      name: string;
      country: string;
      title: string;
    },
  ];
  carrier?: {
    id: number;
    name: string;
    terms_of_payment: {
      id: number;
      name: string;
    };
  };
  customers: {
    id: number;
    name: string;
  }[];
  supplier?: {
    id: number;
    name: string;
    terms_of_payment: {
      id: number;
      name: string;
    };
  };
  packages: [
    {
      id: number;
      name: string;
    },
  ];
  special_agreement?: string[];
  deleted_at?: string | null;
};

export enum GeneralInfoLabel {
  SERVICE_TYPE = "Service Type",
  SERVICE_MASTER = "Service Master",
  PORT = "Port",
  CARRIER = "Carrier",
  COMMODITIES = "Commodities",
  TERMINAL = "Terminal",
  SUPPLIER = "Supplier",
}
export enum LocationLabel {
  WAREHOUSES = "Warehouses",
  TERMINALS = "Terminals",
  CITIES = "Cities",
  PORTS = "Ports",
}
export enum PriceBlockLabel {
  NAME = "Sub-service Description",
  PRICE = "Price",
  ADDITIONAL_DELAY = "Additional Surcharge Delay Request per Cntr",
  CURRENCY = "Currency",
  PRICE_UNIT = "Price Unit",
  EQUIPMENT = "Equipment",
  AVG_WEIGHT = "Average Weight",
  AVG_WEIGHT_PRICE_UNIT = "Average Weight Price Unit",
  AVG_WEIGHT_UNIT = "Average Weight Unit",
  MIN_CHARGEABLE = "Min Chargeable Value",
  MIN_CHARGEABLE_PRICE_UNIT = "Min Chargeable Value Price Unit",
  MIN_CHARGEABLE_UNIT = "Min Chargeable Value Unit",
  MAX_CHARGEABLE = "Max Chargeable Value",
  MAX_CHARGEABLE_PRICE_UNIT = "Max Chargeable Value Price Unit",
  MAX_CHARGEABLE_UNIT = "Max Chargeable Value Unit",
  MAX_INSURANCE_VALUE_PER_TON = "Max insurance value per ton",
  MAX_INSURANCE_VALUE_PER_TRUCK = "Max insurance value per truck",
  MAX_INSURANCE_VALUE_TOTAL = "Max insurance value total",
  RANGE_START = "Range Start",
  RANGE_END = "Range End",
  RANGE_UNIT = "Range Unit",
}
export enum OtherLabel {
  EXPIRY_DATE = "Expiry Validity Date",
  SPECIAL_AGREEMENT = "Special Agreement",
  MACRO_SERVICE = "Macro Service",
  NOTE = "Note",
}
